/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit'
import { Field, ZapState } from 'state/types'
import { getAddress } from 'utils/addressHelpers'
import zapInTokens from 'config/constants/zapInTokens'
import zapOutTokens from 'config/constants/zapOutTokens'
import { fetchTokenBalancesUserData, fetchTokenAllowancesUserData } from './fetchTokenBalancesUserData'

const zapTokens = [
  ...zapInTokens.map((item, index) => ({
    ...item,
    pid: index,
    field: 'input',
  })),
  ...zapOutTokens.map((item, index) => ({
    ...item,
    pid: zapInTokens.length + index,
    field: 'output',
  })),
]

const initialState: ZapState = {
  independentField: Field.INPUT,
  typedValue: '',
  [Field.INPUT]: {
    currencyId: '',
  },
  [Field.OUTPUT]: {
    currencyId: '',
  },
  recipient: null,
  tokenBalances: zapTokens,
  userDataLoaded: false,
}

export const fetchTokenBalancesUserDataAsync =
  (account: string): any =>
  async (dispatch, getState) => {
    const allTokens = zapTokens
    const balances = await fetchTokenBalancesUserData(account, allTokens)
    const allowances = await fetchTokenAllowancesUserData(account, allTokens)
    const prices = getState().farms.prices

    const allTokensBalances = allTokens.map((item, index) => ({
      ...item,
      allowance: allowances[index],
      tokenBalance: balances[index],
      tokenPrice: prices[getAddress(item.address).toLowerCase()],
    }))

    const reqData = {
      tokenBalances: allTokensBalances,
      userDataLoaded: prices['0x90E892FED501ae00596448aECF998C88816e5C0F'.toLowerCase()] > 0,
    }
    dispatch(setTokenBalanceData(reqData))
  }

export const updateUserZapAllowance =
  (pid: number, account: string): any =>
  async (dispatch) => {
    const allTokens = zapTokens
    const allowances = await fetchTokenAllowancesUserData(account, allTokens)
    /*  we have to add one to pid because redux's tokenBalances include native token  */
    const userDataIndex = pid + 1
    dispatch(updateZapUserData({ userDataIndex, field: 'allowance', value: allowances[pid] }))
  }

export const ZapSlice = createSlice({
  name: 'Zap',
  initialState,
  reducers: {
    selectCurrency: (state, action) => {
      const { currencyId, field } = action.payload
      const otherField = field === Field.INPUT ? Field.OUTPUT : Field.INPUT
      if (currencyId === state[otherField].currencyId) {
        // the case where we have to swap the order
        return {
          ...state,
          independentField: state.independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT,
          [field]: { currencyId },
          [otherField]: { currencyId: state[field].currencyId },
        }
      }
      // the normal case
      return {
        ...state,
        [field]: { currencyId },
      }
    },
    switchCurrencies: (state) => {
      return {
        ...state,
        independentField: state.independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT,
        [Field.INPUT]: { currencyId: state[Field.OUTPUT].currencyId },
        [Field.OUTPUT]: { currencyId: state[Field.INPUT].currencyId },
      }
    },
    typeInput: (state, action) => {
      const { typedValue, field } = action.payload
      return {
        ...state,
        independentField: field,
        typedValue,
      }
    },
    setTokenBalanceData: (state, action) => {
      return {
        ...state,
        tokenBalances: action.payload.tokenBalances,
        userDataLoaded: action.payload.userDataLoaded,
      }
    },
    updateZapUserData: (state, action) => {
      const { field, value, pid } = action.payload
      if (field === 'allowance') {
        state.tokenBalances[pid] = {
          ...state.tokenBalances[pid],
          [field]: value,
        }
      }
    },
  },
})

// Actions
export const { selectCurrency, switchCurrencies, typeInput, setTokenBalanceData, updateZapUserData } = ZapSlice.actions

export default ZapSlice.reducer
